import { type Capability } from '@sisoma/auth';
import { isNavGroup, type NavLink, type NavSection } from '@sisoma/ui';

/**
 * Sidebar navigation registry — the single source of truth for what appears
 * in the sidenav.
 *
 * To add a link:
 *   1. Register a route (usually lazy) in `app.routes.ts`.
 *   2. Add an entry below with the same path.
 *   3. Need a new icon? Register it in the `@sisoma/ui` Icon component first
 *      (projects/ui/src/lib/icon/icon.ts).
 *
 * To add a collapsible group, give an entry `children` instead of a `route`.
 * The parent then expands in place when clicked rather than navigating, and
 * opens automatically when one of its children is the active route.
 *
 * ## Capability gating (fp1-04/fp1-05)
 *
 * An entry may carry an optional `capability` — the sidenav only shows it when
 * the active partner grants that capability. Gating lives here, not in
 * `@sisoma/ui`, so the library's `NavLink`/`NavSection` types stay purely
 * presentational and auth-free. Run the registry through {@link filterNav}
 * before handing it to the sidenav (the `Shell` does this reactively).
 *
 * `Members` (fp1-07), `Roles` (fp1-08), `Invites` (fp1-09), `Workspaces`
 * (fp1-10), `Billing` (fp3-01), `Audit Log` (PA-AUDIT) and the footer
 * `Settings` / `Single sign-on` are gated entries — each shows only when the
 * active partner grants the matching capability (`partner:manage_members` /
 * `partner:manage_roles` / `partner:invite` / `partner:manage_workspaces` /
 * `partner:manage_billing` / `partner:view_audit` / `partner:manage_settings` /
 * `partner:manage_sso`).
 *
 * The `Content` group (PA-CONTENT) gates per child rather than on the group:
 * `Categories` needs `content:manage_categories`, `Content` needs
 * `content:view`. `filterNav` drops the whole group when neither is held, so a
 * capability-less member sees nothing and an Editor without
 * `content:manage_categories` sees only the `Content` child.
 */

/** A `NavLink` that may be hidden unless the active partner grants `capability`. */
export interface GatedNavLink extends NavLink {
  capability?: Capability;
}

/** A collapsible group that may itself be gated, with gated child links. */
export interface GatedNavGroup {
  id?: string;
  label: string;
  icon: NavLink['icon'];
  capability?: Capability;
  children: GatedNavLink[];
}

export type GatedNavItem = GatedNavLink | GatedNavGroup;

/** A titled section that may itself be gated, holding gated entries. */
export interface GatedNavSection {
  heading?: string;
  capability?: Capability;
  items: GatedNavItem[];
}

export const NAV_SECTIONS: GatedNavSection[] = [
  {
    items: [
      { label: 'Dashboard', icon: 'dashboard', route: 'dashboard' },
      { label: 'Members', icon: 'users', route: 'members', capability: 'partner:manage_members' },
      { label: 'Roles', icon: 'shield', route: 'roles', capability: 'partner:manage_roles' },
      { label: 'Invites', icon: 'user-plus', route: 'invites', capability: 'partner:invite' },
      {
        label: 'Workspaces',
        icon: 'briefcase',
        route: 'workspaces',
        capability: 'partner:manage_workspaces',
      },
      {
        label: 'Billing',
        icon: 'credit-card',
        route: 'billing',
        capability: 'partner:manage_billing',
      },
      // Audit Log (PA-AUDIT §6). A settings sub-page — the design puts it in the
      // Settings sub-nav, which does not exist yet, so it links from here until it
      // does. `partner:view_audit` is Owner/Manager-only (decision §9.1).
      {
        label: 'Audit Log',
        icon: 'file-text',
        route: 'settings/audit',
        capability: 'partner:view_audit',
      },
    ],
  },
  {
    heading: 'Library',
    items: [
      {
        // Explicit `id` because the group and its second child share the label
        // "Content" — `navItemKey` falls back to a group's label, and relying on
        // display text for identity breaks on a rename.
        id: 'content',
        label: 'Content',
        icon: 'layers',
        children: [
          {
            label: 'Categories',
            icon: 'tag',
            route: 'content/categories',
            capability: 'content:manage_categories',
          },
          {
            label: 'Content',
            icon: 'content',
            route: 'content/library',
            capability: 'content:view',
          },
        ],
      },
    ],
  },
];

/** Links pinned to the bottom of the sidenav. Groups are not supported here. */
export const FOOTER_NAV_ITEMS: GatedNavLink[] = [
  { label: 'Settings', icon: 'settings', route: 'settings', capability: 'partner:manage_settings' },
  // A settings sub-page, but linked directly: `partner:manage_sso` can be held
  // on its own by an IT delegate who has no `partner:manage_settings` and so
  // never sees the Settings entry above it.
  {
    label: 'Single sign-on',
    icon: 'key',
    route: 'settings/sso',
    capability: 'partner:manage_sso',
  },
];

/**
 * Drop every entry the active partner cannot see, and return the plain
 * presentational `NavSection[]` the sidenav consumes.
 *
 * Pure — `has` is the only source of reactivity. An entry with no `capability`
 * is always kept; a group is kept only if it passes its own gate and still has
 * at least one visible child; a section that empties out is dropped.
 */
export function filterNav(
  sections: readonly GatedNavSection[],
  has: (capability: Capability) => boolean,
): NavSection[] {
  const allowed = (item: { capability?: Capability }): boolean =>
    item.capability === undefined || has(item.capability);

  return sections
    .filter(allowed)
    .map((section) => ({
      heading: section.heading,
      items: section.items.filter(allowed).flatMap((item): GatedNavItem[] => {
        if (!isGatedGroup(item)) {
          return [item];
        }
        const children = item.children.filter(allowed);
        return children.length > 0 ? [{ ...item, children }] : [];
      }),
    }))
    .filter((section) => section.items.length > 0);
}

/** Filter the footer links the same way (flat — no groups). */
export function filterNavLinks(
  links: readonly GatedNavLink[],
  has: (capability: Capability) => boolean,
): NavLink[] {
  return links.filter((link) => link.capability === undefined || has(link.capability));
}

/** True when a gated entry expands rather than navigates. Mirrors ui `isNavGroup`. */
function isGatedGroup(item: GatedNavItem): item is GatedNavGroup {
  return isNavGroup(item);
}
